import { useState } from "react";
import BreadCrumbForm from "./common-components/BreadCrumbForm";
import FormHeading from "./common-components/FormHeading";
import PersonalDetails from "./interview-assessment-form/PersonalDetails";
import EducationalDetails from "./interview-assessment-form/EducationalDetails";
import { EmploymentHistorySection } from "./interview-assessment-form/EmploymentDetails";
import EvaluationSheet, {
  EvaluationFormData,
  evaluationInitialData,
  toApiPayload,
} from "./interview-assessment-form/EvaluationSheet";
import ReferencesDetails from "./interview-assessment-form/References";
import FamilyDetailsForm from "./interview-assessment-form/FamilyDetailsForm";
import {
  EducationDetails,
  EmployeeRelations,
  EmploymentHistory,
  FamilyDetails,
  PrevApplications,
  References,
} from "../../types/Form-types";
import { InitialInterviewData } from "./interview-assessment-form/state-data";
import useApi from "../../core/customhook/useApi";
import Swal from "sweetalert2";
import {
  EvaluationFormErrors,
  getFirstErrorMessage,
  InterviewFormErrors,
  validateEvaluationForm,
  validateInterviewForm,
} from "./validation/form-validations/Interview-validation";
import LanguageDetails from "./interview-assessment-form/LanguageDetails";
import Source_PositionDetails from "./interview-assessment-form/Source_PositionDetails";
import HrFinalRecommendation from "./interview-assessment-form/HrFinalRecommendation";
// import {
//   EvaluationFormErrors,
//   validateEvaluationForm,
// } from "./validation/form-validations/Evaluation-validation";
import axios from "axios";
import { apibaseurl } from "../../environment";
import { all_routes } from "../router/all_routes";

export type InterviewAssessmentTypes = {
  source: string;
  position: string;

  candidateName: string;
  gender: string;
  dateOfBirth: string;
  department: string;
  Address: string;
  permanentAddress: string;
  mobileNo: string;
  maritalStatus: string;
  emailId: string;

  langSpeak: string[];
  langRead: string[];
  langWrite: string[];

  careerGoal: string;
  strength: string;
  weakness: string;
  relationShip: boolean;
  applied_previously: boolean;

  current_ctc: number | string;
  expected_ctc: number | string;
  notice_period_days: number | string;
  convicted_in_court: string;

  final_hr_recommendation: string;
  grade_designation_allocated: string;
  ctc_to_be_offered: number | string;
  onboarding_date: string;

  education_details: EducationDetails[];
  family_details: FamilyDetails[];
  employment_history: EmploymentHistory[];
  employee_relations: EmployeeRelations[];
  prev_applications: PrevApplications[];
  references: References[];
};

const InterviewAssessmentForm = () => {
  const [sumbitLoading, setSubmitLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<InterviewFormErrors>({});
  const [formData, setFormData] =
    useState<InterviewAssessmentTypes>(InitialInterviewData);

  // ── Evaluation state lives in parent ──────────────────────────────
  const [evaluationData, setEvaluationData] = useState<EvaluationFormData>(
    evaluationInitialData,
  );
  const [evaluationErrors, setEvaluationErrors] =
    useState<EvaluationFormErrors>({});

  // -- API
  const {
    callApi: interviewSubmitApi,
    loading,
    error,
  } = useApi("/api/save-interview-assessment", {
    method: "POST",
    autoCall: false,
  });
  // -- end Api

  // const handleError = (name: keyof InterviewAssessmentTypes) =>
  // const handleError = (name: string) =>
  //   setErrors((prev: any) => {
  //     const { [name]: _, ...rest } = prev;
  //     return rest;
  //   });

  const handleError = (name: string) =>
    setErrors((prev: any) => {
      // ─── nested array error e.g. "education_details.0.qualification"
      if (name.includes(".")) {
        const [section, indexStr, field] = name.split(".");
        const index = Number(indexStr);

        if (!prev[section] || !Array.isArray(prev[section])) return prev;

        const updatedSection = prev[section].map(
          (rowErrors: any, i: number) => {
            if (i !== index) return rowErrors;
            const { [field]: _, ...rest } = rowErrors ?? {};
            return rest;
          },
        );

        return { ...prev, [section]: updatedSection };
      }

      // ─── flat key (existing behavior)
      const { [name]: _, ...rest } = prev;
      return rest;
    });

  const handleDateChange = (name: string, value: string) => {
    const [day, month, year] = value?.split("-");
    const formatted = `${year}-${month}-${day}`;
    setFormData((prev) => ({ ...prev, [name]: formatted }));
    handleError(name as keyof InterviewAssessmentTypes);
  };

  const handleChange = (e: React.ChangeEvent<any>) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({
      ...prev,
      [name]: value,
    }));
    handleError(name as keyof InterviewAssessmentTypes);
  };

  const handleFieldChange = (name: string, value: string) => {
    setFormData((prev: InterviewAssessmentTypes) => ({
      ...prev,
      [name]:
        prev[name as keyof InterviewAssessmentTypes] == value ? "" : value,
    }));
    handleError(name as keyof InterviewAssessmentTypes);
  };

  // handlesubmit form data
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log(formData);

    const interviewErrors = validateInterviewForm(formData);
    const evalErrors = validateEvaluationForm(evaluationData);

    setErrors(interviewErrors);
    setEvaluationErrors(evalErrors);

    // console.log("interview errors:", evalErrors);

    // check if any flat error exists
    const firstError = getFirstErrorMessage(interviewErrors, evalErrors);
    if (firstError) {
      Swal.fire({
        icon: "error",
        title: "Validation Error!",
        text: firstError,
        confirmButtonText: "OK",
        confirmButtonColor: "#fd7e14",
      });
      return;
    }

    setSubmitLoading(true);
    // const { langSpeak, langRead, langWrite, ...restData } = formData;
    try {
      // const response = await interviewSubmitApi(restData);
      const response = await interviewSubmitApi(formData);
      if (!response.success) return;
      const assessmentId = response.data.assessment_id;
      const finalResult = await handleEvaluationSubmit(assessmentId);

      if (finalResult.data.success) {
        Swal.fire({
          icon: "success",
          title: "Submitted!",
          text: finalResult.data.message ?? "Form submitted successfully",
          confirmButtonText: "OK",
          confirmButtonColor: "#fd7e14",
        });
      }

      setFormData(InitialInterviewData);
      setEvaluationData(evaluationInitialData);
    } catch (error: any) {
      console.log("error message", error.response);
      const errorMsg = error.response?.data?.message ?? null;
      Swal.fire({
        icon: "error",
        title: "Failed!",
        text: errorMsg,
        confirmButtonText: "OK",
        confirmButtonColor: "#fd7e14",
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  // handleEvaluationSubvmit
  const handleEvaluationSubmit = async (assessmentId: number) => {
    const payload = toApiPayload(evaluationData);

    const response: any = await axios.post(
      `${apibaseurl}/api/evalute-interview-assessment/${assessmentId}/evaluation`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token") ?? ""}`,
        },
      },
    );
    return response;
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <BreadCrumbForm
          title="Interview Assessment Form"
          linkTitle="Interview Assessment Form"
          hasBackButton={true}
        // backLink={`${all_routes.employeeAssessmentForms}/interview`}
        />

        <div className="card shadow-sm mt-4 form-card-container">
          <div className="card-body p-4 p-md-5">
            <form onSubmit={handleSubmit}>
              <FormHeading title="Interview Assessment Form" />

              <Source_PositionDetails
                formData={formData}
                setFormData={setFormData}
                errors={errors}
                handleFieldChange={handleFieldChange}
              />
              <PersonalDetails
                formData={formData}
                setFormData={setFormData}
                errors={errors}
                handleChange={handleChange}
                handleDateChange={handleDateChange}
                handleFieldChange={handleFieldChange}
              />
              <LanguageDetails
                formData={formData}
                setFormData={setFormData}
                errors={errors}
                handleFieldChange={handleFieldChange}
              />
              <EducationalDetails
                formData={formData}
                setFormData={setFormData}
                errors={errors}
                handleError={handleError}
              />
              <FamilyDetailsForm
                formData={formData}
                setFormData={setFormData}
                errors={errors?.family_details}
                handleError={handleError}
              />
              <EmploymentHistorySection
                formData={formData}
                setFormData={setFormData}
                errors={errors}
                handleChange={handleChange}
                handleFieldChange={handleFieldChange}
                handleError={handleError}
              />
              <ReferencesDetails
                formData={formData}
                setFormData={setFormData}
                errors={errors?.references}
                handleError={handleError}
              />
              <EvaluationSheet
                formData={evaluationData}
                setFormData={setEvaluationData}
                errors={evaluationErrors}
                setErrors={setEvaluationErrors}
              />
              <HrFinalRecommendation
                formData={formData}
                setFormData={setFormData}
                errors={errors}
                // handleChange={handleChange}
                handleDateChange={handleDateChange}
                handleFieldChange={handleFieldChange}
              />

              <div className="text-end">
                <button
                  type="submit"
                  disabled={
                    // Object.keys(errors)?.length > 0 ||
                    // Object.keys(evaluationErrors)?.length > 0 ||
                    loading || sumbitLoading
                  }
                  className="btn btn-primary"
                >
                  {/* Submit */}
                  {sumbitLoading ? "Submitting..." : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <div className="footer d-flex align-items-center justify-content-center border-top bg-white p-3">
        <p className="mb-0"> © {new Date().getFullYear()} WeConnect. All rights reserved.</p>
      </div>
    </div>
  );
};

export default InterviewAssessmentForm;

//  try {
//       const response = await interviewSubmitApi(formData);
//       if (response.success) {
//         Swal.fire({
//           icon: "success",
//           title: "Submitted!",
//           text: "Form submitted successfully",
//           confirmButtonText: "OK",
//           confirmButtonColor: "#fd7e14",
//         });
//         // setFormData(InitialInterviewData);
//       }
//       // console.log(response);
//     } catch (error: any) {
//       console.log("error message", error.response);
//       const errorMsg = error.response?.data?.message ?? null;
//       Swal.fire({
//         icon: "error",
//         title: "Failed!",
//         text: errorMsg ?? "Failed to submit form data.",
//         confirmButtonText: "OK",
//         confirmButtonColor: "#fd7e14",
//       });
//     }

// error rerrer
// const getFirstErrorMessage = (
//   interviewErrors: InterviewFormErrors,
//   evalErrors: EvaluationFormErrors,
// ): string | null => {
//   const arrayKeys = [
//     "education_details",
//     "family_details",
//     "employment_history",
//     "employee_relations",
//     "prev_applications",
//     "references",
//   ];

//   // ── first flat error ──────────────────────────────────────────────
//   const firstFlatEntry = Object.entries(interviewErrors)
//     .filter(([key]) => !arrayKeys.includes(key))
//     .find(([_, v]) => typeof v === "string" && v !== "");

//   if (firstFlatEntry) {
//     const [key, msg] = firstFlatEntry;
//     const label = FieldLabels[key] ?? key;
//     return `${label}: ${msg}`; // ✅ use actual msg
//   }

//   // ── first array error ─────────────────────────────────────────────
//   for (const key of arrayKeys) {
//     const rows: any[] = (interviewErrors as any)[key] ?? [];
//     for (const row of rows) {
//       const firstRowEntry = Object.entries(row).find(
//         ([_, v]) => typeof v === "string" && v !== "",
//       );
//       if (firstRowEntry) {
//         const [field, msg] = firstRowEntry;
//         const label = FieldLabels[field] ?? field;
//         return `${label}: ${msg}`; // ✅ use actual msg
//       }
//     }
//   }

//   // ── first eval error ──────────────────────────────────────────────
//   for (const [section, value] of Object.entries(evalErrors)) {
//     if (typeof value === "object" && value !== null) {
//       const firstEntry = Object.entries(value).find(
//         ([_, v]) => typeof v === "string" && v !== "",
//       );
//       if (firstEntry) {
//         const [field, msg] = firstEntry;
//         const label = FieldLabels[field] ?? field;
//         return `${label}: ${msg}`; // ✅ use actual msg
//       }
//     }
//   }

//   return null;
// };
